/**
 * The before/after diff — the default result display for every cleaning tool.
 *
 * Two things it must get right, both from docs/USER_JOURNEY.md and
 * UI_UX_BRIEF:
 *
 *   * **Stacked on mobile, side-by-side on desktop.** Two columns at 360px is
 *     unreadable. The layout is CSS (`.diff__panes` becomes two columns above
 *     700px); this module renders one structure that works either way, so
 *     there is no breakpoint logic in JavaScript to disagree with the
 *     stylesheet.
 *   * **A per-cue change marker.** On a stacked layout the reader cannot see
 *     the two panes at once, so the marker carries the verdict on its own —
 *     with a glyph AND a word, never colour alone.
 *
 * Virtualised on the same core as the cue table, for the same reason: a
 * sentence-case pass over a three-hour ASS file modifies tens of thousands of
 * cues, and a diff that renders them all is a diff nobody can scroll.
 */

import { data, qs, setData } from './dom.ts';
import { poolSize, type Range, sameRange, sizerHeight, slotFor, windowRange } from './virtual.ts';
import { wordDiff } from './word-diff.ts';

/**
 * 'restructured' is split/merge/resegment and is NOT add-plus-remove. CLEAN
 * derives the lineage once and excludes those cues from added/removed, so a
 * view without this kind describes the data structure rather than what
 * happened — and one cue split in two is the most common thing this does.
 */
export type ChangeKind = 'edited' | 'added' | 'removed' | 'restructured' | 'unchanged' | 'moved';

export interface DiffRow {
  readonly n: number;
  readonly time: string;
  readonly change: ChangeKind;
  readonly before: string;
  readonly after: string;
}

export interface DiffSource {
  count: () => number;
  row: (index: number) => DiffRow | null;
  /** Rendered in the legend. Counts come from `DocDiff`, not from this view. */
  summary: () => string;
}

export interface DiffHandle {
  refresh: () => void;
  destroy: () => void;
}

const OVERSCAN = 4;
const FALLBACK_ROW_HEIGHT = 132;

/** Glyph + word. The glyph alone is a colour substitute; the word is the fact. */
const MARKER_TEXT: Readonly<Record<ChangeKind, string>> = {
  edited: '~ changed',
  added: '+ added',
  removed: '− removed',
  restructured: '⇄ split or merged',
  unchanged: '= unchanged',
  // A split's part two: not in part one, and not gone either.
  moved: '→ part 2',
};

interface RowNodes {
  root: HTMLElement;
  meta: HTMLElement;
  marker: HTMLElement;
  before: HTMLElement;
  after: HTMLElement;
  /** The row index this element currently renders, or -1 when parked. */
  index: number;
}

function buildRow(beforeLabel: string, afterLabel: string): RowNodes {
  const root = document.createElement('div');
  root.className = 'diff__row cue-table__row--virtual';

  const meta = document.createElement('p');
  meta.className = 'diff__meta';
  const n = document.createElement('span');
  const time = document.createElement('span');
  const marker = document.createElement('span');
  marker.className = 'diff__marker';
  meta.append(n, time, marker);

  const panes = document.createElement('div');
  panes.className = 'diff__panes';

  const pane = (mod: string, label: string) => {
    const wrapper = document.createElement('div');
    wrapper.className = `diff__pane diff__pane--${mod}`;
    const caption = document.createElement('span');
    caption.className = 'diff__pane-label';
    caption.textContent = label;
    const body = document.createElement('span');
    wrapper.append(caption, body);
    panes.append(wrapper);
    return body;
  };

  const before = pane('before', beforeLabel);
  const after = pane('after', afterLabel);
  root.append(meta, panes);
  // Stash the two meta spans on the row so paint() does not re-query them.
  (root as HTMLElement & { _n?: HTMLElement; _time?: HTMLElement })._n = n;
  (root as HTMLElement & { _n?: HTMLElement; _time?: HTMLElement })._time = time;
  return { root, meta, marker, before, after, index: -1 };
}

/** Render inline marks into a pane. `side` picks which pieces are visible. */
function paintPane(
  target: HTMLElement,
  before: string,
  after: string,
  side: 'before' | 'after',
): void {
  target.replaceChildren();
  const pieces = wordDiff(before, after);
  for (const piece of pieces) {
    if (side === 'before' && piece.kind === 'ins') continue;
    if (side === 'after' && piece.kind === 'del') continue;
    if (piece.kind === 'same') {
      target.append(document.createTextNode(piece.text));
      continue;
    }
    const mark = document.createElement(piece.kind === 'del' ? 'del' : 'ins');
    mark.textContent = piece.text;
    target.append(mark);
  }
}

export function mountDiffView(container: HTMLElement, source: DiffSource): DiffHandle {
  const rowsHost = qs<HTMLElement>('[data-diff-rows]', container);
  const empty = qs<HTMLElement>('[data-diff-empty]', container);
  const summaryEl = qs<HTMLElement>('[data-diff-summary]', container);
  if (!rowsHost) throw new Error('diff view: markup is missing [data-diff-rows]');

  const beforeLabel = data(container, 'before-label') ?? 'Before';
  const afterLabel = data(container, 'after-label') ?? 'After';

  // The scroller/sizer/window scaffold is built here rather than in the macro,
  // because a diff with no JavaScript has nothing to show and an empty
  // scroll box would just be a grey rectangle on a crawler's render.
  const scroller = document.createElement('div');
  scroller.className = 'cue-table__scroller';
  scroller.tabIndex = 0;
  const sizer = document.createElement('div');
  sizer.className = 'cue-table__sizer';
  const windowEl = document.createElement('div');
  windowEl.className = 'cue-table__window';
  sizer.append(windowEl);
  scroller.append(sizer);
  rowsHost.replaceChildren(scroller);

  const pool: RowNodes[] = [];
  let rowHeight = FALLBACK_ROW_HEIGHT;
  let rendered: Range = { start: -1, end: -1, offset: 0 };
  let frame = 0;
  let destroyed = false;

  function measure(): void {
    const probe = pool[0];
    if (!probe || probe.root.hidden) return;
    // See cue-table.ts measure(): a zero-width container makes every row wrap
    // to one character and measure at many times its real height.
    if (scroller.clientWidth === 0 || scroller.clientHeight === 0) return;
    const height = probe.root.getBoundingClientRect().height;
    if (height > 0 && Math.abs(height - rowHeight) > 1) {
      rowHeight = height;
      sizer.style.height = `${sizerHeight(source.count(), rowHeight)}px`;
      // Every row's position was computed from the old height. Without this
      // the paint that follows skips every row already showing its index, so
      // they keep the fallback pitch and a row taller than 132px overlaps the
      // next one. cue-table.ts has always done this; this copy had not.
      for (const row of pool) row.index = -1;
    }
  }

  function ensurePool(size: number): void {
    while (pool.length < size) {
      const row = buildRow(beforeLabel, afterLabel);
      pool.push(row);
      windowEl.append(row.root);
    }
    for (let i = size; i < pool.length; i += 1) {
      const row = pool[i];
      if (row && !row.root.hidden) {
        row.root.hidden = true;
        row.index = -1;
      }
    }
  }

  function paint(): void {
    if (destroyed) return;
    const count = source.count();
    const viewport = scroller.clientHeight || 1;
    const range = windowRange(scroller.scrollTop, viewport, rowHeight, count, OVERSCAN);
    const wanted = Math.min(poolSize(viewport, rowHeight, OVERSCAN), Math.max(count, 1));
    const grew = wanted > pool.length;
    ensurePool(wanted);
    if (grew) for (const row of pool) row.index = -1;

    // Modulo slot assignment, same as the cue table and for a sharper reason:
    // repainting a row here runs a word-level diff over both panes, so
    // rewriting the whole window on every frame would be an LCS per visible
    // row per frame.
    for (let index = range.start; index < range.end; index += 1) {
      const node = pool[slotFor(index, pool.length)];
      if (!node || node.index === index) continue;
      const data = source.row(index);
      if (!data) {
        node.root.hidden = true;
        node.index = -1;
        continue;
      }
      node.root.hidden = false;
      node.root.style.transform = `translate3d(0, ${index * rowHeight}px, 0)`;
      const meta = node.root as HTMLElement & { _n?: HTMLElement; _time?: HTMLElement };
      if (meta._n) meta._n.textContent = `#${data.n}`;
      if (meta._time) meta._time.textContent = data.time;
      setData(node.marker, 'change', data.change);
      node.marker.textContent = MARKER_TEXT[data.change];
      paintPane(node.before, data.before, data.after, 'before');
      paintPane(node.after, data.before, data.after, 'after');
      node.index = index;
    }
    for (const node of pool) {
      if (node.index >= range.start && node.index < range.end) continue;
      if (node.root.hidden) continue;
      node.root.hidden = true;
      node.index = -1;
    }
    rendered = range;
  }

  function onScroll(): void {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const next = windowRange(
        scroller.scrollTop,
        scroller.clientHeight || 1,
        rowHeight,
        source.count(),
        OVERSCAN,
      );
      if (sameRange(next, rendered)) return;
      paint();
    });
  }

  // Re-measure when the box changes size, as the cue table does. The row's
  // height depends on the box: the panes sit side by side above a container
  // width and stack below it, so a rotated phone or a resized window changes
  // it. And the first refresh usually runs while the result section is still
  // hidden (tool.ts reveals it after), when there is nothing to measure; the
  // reveal is a resize from zero, and this is what catches it.
  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          measure();
          paint();
        })
      : null;

  scroller.addEventListener('scroll', onScroll, { passive: true });
  resizeObserver?.observe(scroller);

  const handle: DiffHandle = {
    refresh() {
      const count = source.count();
      if (empty) empty.hidden = count > 0;
      scroller.hidden = count === 0;
      if (summaryEl) summaryEl.textContent = source.summary();
      sizer.style.height = `${sizerHeight(count, rowHeight)}px`;
      rendered = { start: -1, end: -1, offset: 0 };
      for (const row of pool) row.index = -1;
      paint();
      measure();
      paint();
    },
    destroy() {
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', onScroll);
      resizeObserver?.disconnect();
      pool.length = 0;
      rowsHost.replaceChildren();
    },
  };

  handle.refresh();
  return handle;
}
